import React, { useState, useEffect } from "react";
import ReactGA from "react-ga4";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { useAuth } from "../contexts/AuthContext";

const { REACT_APP_API } = process.env;

const ProductDetails = ({ product, setSelectedProduct }) => {
  const {
    title,
    description,
    image,
    minPrice,
    brand,
    maxPrice,
    link,
    features,
  } = product;

  const [expandDescription, setExpandDescription] = useState(false);
  const [browserView, setBrowserView] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [viewportSize, setViewportSize] = useState({
    width: 1280,
    height: 800,
  });
  const [isAgenticPurchaseEnabled, setIsAgenticPurchaseEnabled] =
    useState(false);
  const { getAccessToken } = useAuth();

  // Check for agentic purchase feature flag in URL
  useEffect(() => {
    const url = new URL(window.location.href);
    const hasAgenticPurchase = url.searchParams.has("agenticPurchase");
    setIsAgenticPurchaseEnabled(hasAgenticPurchase);
  }, []);

  // Get current viewport dimensions when component loads
  useEffect(() => {
    const updateViewportSize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial size
    updateViewportSize();

    // Update on resize
    window.addEventListener("resize", updateViewportSize);
    return () => window.removeEventListener("resize", updateViewportSize);
  }, []);

  // Cleanup any active browser session when component unmounts
  useEffect(() => {
    // We need to define a cleanup function that doesn't depend on the current `terminateBrowserSession`
    const cleanup = async () => {
      if (sessionId) {
        try {
          console.log(
            "Component unmounting, terminating browser session:",
            sessionId
          );
          const token = await getAccessToken();

          await fetch(`${REACT_APP_API}/secure/browser/${sessionId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
          });
        } catch (error) {
          console.error("Error during cleanup:", error);
        }
      }
    };

    return () => {
      cleanup().catch((err) => console.error("Cleanup failed:", err));
    };
  }, [sessionId, getAccessToken]);

  const terminateBrowserSession = async () => {
    if (!sessionId) return;

    try {
      console.log("Terminating browser session:", sessionId);
      const token = await getAccessToken();

      await fetch(`${REACT_APP_API}/secure/browser/${sessionId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      console.log("Browser session terminated");
      setSessionId(null);
    } catch (error) {
      console.error("Error terminating browser session:", error);
      // Don't show an error to the user for this operation
    }
  };

  const startBrowserSession = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await getAccessToken();
      console.log("Starting browser session for URL:", link);

      // Start a browser session through our proxy endpoint
      const sessionResponse = await fetch(`${REACT_APP_API}/secure/browser`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          startUrl: link,
          viewport: viewportSize,
        }),
        credentials: "include",
      });

      const sessionData = await sessionResponse.json();

      if (!sessionResponse.ok) {
        console.error("Browser session error:", sessionData);
        throw new Error(sessionData.error || "Failed to start browser session");
      }

      const browserId = sessionData.sessionId;
      if (!browserId) {
        throw new Error("No session ID returned from the API");
      }

      // Save the session ID for later cleanup
      setSessionId(browserId);

      // If we have a live view URL, show it immediately
      if (sessionData.sessionUrl) {
        console.log("Got immediate browser view URL:", sessionData.sessionUrl);
        setBrowserView(
          `https://anchorforge.io/devtools-frontend/inspector-fullscreen.html?wss=${sessionData.sessionUrl}`
        );
        setIsLoading(false); // Turn off loading once we have the iframe
      }

      console.log("Browser session created with ID:", browserId);

      // Perform the web task to add item to cart and proceed to checkout in the background
      console.log("Performing checkout task for product:", title);
      (async () => {
        try {
          const response = await fetch(
            `${REACT_APP_API}/secure/browser/${browserId}/task`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                task: `Navigate to ${link}, find the product "${title}", add it to cart, and proceed to checkout. Stop when you are on the checkout screen, do not complete any details whilst we wait for the user to enter their details`,
                url: link, // Explicitly provide the URL to navigate to
              }),
              credentials: "include",
            }
          );

          if (!response.ok) {
            const data = await response.json();
            console.error("Browser task error:", data);
            throw new Error(data.error || "Failed to perform task");
          }

          const taskData = await response.json();
          console.log("Task completed successfully:", taskData);
          return taskData;
        } catch (error) {
          console.error("Task failed:", error);
          setError(`Task failed: ${error.message}`);
        }
      })();

      // If we didn't get the live view URL from the session creation,
      // we can still try to get it separately
      if (!sessionData.sessionUrl) {
        console.log("Getting browser view URL");
        try {
          const viewResponse = await fetch(
            `${REACT_APP_API}/secure/browser/${browserId}/view`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              credentials: "include",
            }
          );

          const viewData = await viewResponse.json();

          if (!viewResponse.ok) {
            console.error("Browser view error:", viewData);
            throw new Error(viewData.error || "Failed to get view URL");
          }

          console.log({ viewData });

          if (!viewData.sessionUrl) {
            throw new Error("No live view URL returned from the API");
          }

          console.log("Got browser view URL:", viewData.sessionUrl);
          setBrowserView(viewData.sessionUrl);
          setIsLoading(false); // Turn off loading once we have the iframe
        } catch (viewError) {
          console.error("Failed to get view URL:", viewError);
          // If we failed to get the view URL but we have a session, show a more specific error
          if (sessionData.sessionUrl) {
            throw viewError;
          }
        }
      }

      ReactGA.event({
        category: "Browser Session",
        action: "Start",
        label: link,
      });

      // Ensure loading is turned off if we have the view
      if (browserView) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error starting browser session:", error);
      setError(error.message || "An unexpected error occurred");
      setIsLoading(false);
    }
  };

  return (
    <div className="p-0 relative bg-white flex flex-col flex-1 overflow-y-auto font-sans h-full">
      <div className="relative w-full pt-[100%] overflow-hidden mb-4">
        <img
          src={
            Array.isArray(image)
              ? image[0]
              : image || "/images/placeholder.svg"
          }
          alt={title || "Product image"}
          className="absolute inset-0 w-full my-auto object-cover"
          loading="lazy"
        />
      </div>
      <div className="px-6 py-2 pb-28">
        <header className="flex flex-col gap-2">
          <div className="text-2xl font-light text-penta-black">{title}</div>
          <div className="text-lg font-light text-penta-grey-medium">
            {maxPrice && minPrice !== maxPrice
              ? `$${minPrice} - $${maxPrice}`
              : `$${minPrice || maxPrice}`}
          </div>
          <div className="text-md pt-2 pb-4 text-penta-grey-medium font-light">
            Brand: <span className="text-penta-black font-light">{brand}</span>
          </div>
        </header>
        <section className="flex flex-col gap-6">
          <div>
            <h3 className="text-md mb-2 font-mono text-penta-grey-medium uppercase tracking-wide text-sm">
              Description
            </h3>
            <div className="prose font-light text-penta-grey-medium">
              {description.length > 225 ? (
                <>
                  {expandDescription
                    ? description
                    : description.slice(0, 200) + "..."}
                  <button
                    onClick={() => setExpandDescription(!expandDescription)}
                    className="text-penta-black hover:text-penta-mint ml-1 transition-colors duration-200"
                  >
                    {expandDescription ? "Read less" : "Read more"}
                  </button>
                </>
              ) : (
                description
              )}
            </div>
          </div>

          {features?.length > 0 && (
            <>
              <div className="h-px bg-neutral-200" />
              <div>
                <h3 className="text-md mb-2 font-mono text-penta-grey-medium uppercase tracking-wide text-sm">
                  Product features
                </h3>
                <ul className="flex flex-col gap-2 list-disc list-outside ml-4">
                  {features.map((item) => (
                    <li
                      key={item}
                      className="prose text-penta-grey-medium font-light marker:text-penta-grey-medium 
                      pl-2"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </section>
      </div>

      {(browserView || isLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white w-full h-full flex flex-col overflow-hidden">
            <div className="p-4 flex justify-between items-center border-b">
              <h3 className="text-lg font-medium">
                {browserView ? "Complete Your Purchase" : "Starting Browser..."}
                {isLoading && (
                  <span className="inline-block ml-2 text-penta-mint">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </span>
                )}
              </h3>
              <button
                onClick={() => {
                  setBrowserView(null);
                  terminateBrowserSession();
                  setIsLoading(false);
                }}
                className="text-penta-grey-medium hover:text-penta-black transition-colors duration-200"
              >
                <XMarkIcon width={24} height={24} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              {browserView ? (
                <iframe
                  src={browserView}
                  className="w-full h-full border-0"
                  title="Browser Session"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <svg
                        className="animate-spin h-12 w-12 text-penta-mint"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                    <p className="text-penta-black text-lg">
                      Starting browser session...
                    </p>
                    <p className="text-penta-grey-medium text-sm mt-2">
                      This may take a few moments
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed inset-x-0 bottom-20 z-40 flex justify-center">
          <div className="bg-penta-grey-light border border-penta-coral text-penta-coral px-4 py-3 max-w-md">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-penta-coral mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="absolute top-1 right-1 text-penta-coral hover:opacity-70 transition-opacity duration-200"
            >
              <XMarkIcon width={16} height={16} />
            </button>
          </div>
        </div>
      )}

      <footer className="py-6 flex flex-grow fixed bottom-0 justify-center w-full md:w-[400px]">
        <div className="absolute inset-0 z-0 bg-white" />
        <div className="relative z-10 flex items-center justify-center md:justify-start gap-3">
          <button
            className="w-12 h-12 flex items-center justify-center rounded-full border border-neutral-200 p-1 bg-white hover:border-penta-black transition-colors duration-200"
            onClick={() => {
              setSelectedProduct(null);
            }}
          >
            <XMarkIcon
              width={24}
              height={24}
              className="text-penta-black w-5 h-5"
            />
          </button>
          <a
            href={link}
            onClick={() => {
              ReactGA.event({
                category: "External Product Link",
                action: "Click",
                label: link,
              });
            }}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center h-12 px-8 font-light rounded-full bg-penta-mint text-penta-black text-lg hover:opacity-90 transition-opacity duration-200"
          >
            Visit Site
          </a>
          {isAgenticPurchaseEnabled && (
            <button
              onClick={startBrowserSession}
              disabled={isLoading}
              className="flex items-center justify-center h-12 px-8 font-light rounded-full bg-penta-black text-white text-lg hover:opacity-90 transition-opacity duration-200 disabled:opacity-70"
            >
              {isLoading ? "Starting..." : "Buy Now"}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ProductDetails;
